import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Loader2,
  Crown,
  Calendar,
  TrendingUp,
  Flame,
  Trophy,
  Search,
  Share2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { geminiJSON } from "../lib/gemini";
import { useNavigate } from "react-router-dom";
import { useShareSheet } from "../components/ShareSheet";
import ProductChips from "../components/ProductChips";

/* ------------------------- glow score calculator -------------------------
   Combines: routine streak (40%), days logged (20%), mood positivity (20%),
   challenge completions (20%). Capped at 100.                            */
function calcGlowScore({ streak, daysLogged, routinesCompleted, challengeRows, moods }) {
  const streakScore = Math.min(streak * 4, 40); // 10-day streak = full 40
  const logsScore = Math.min(daysLogged * 1, 20); // 20 logged days = full
  const moodAvg =
    moods.length === 0
      ? 7
      : moods.reduce((s, m) => s + ({ "✨": 10, "💖": 9, "🌿": 8, "😌": 7, "🌧️": 5 }[m] || 7), 0) / moods.length;
  const moodScore = (moodAvg / 10) * 20;
  const completed = challengeRows.filter((c) => c.completed).length;
  const challengeScore = Math.min(completed * 7 + routinesCompleted * 0.2, 20);
  return Math.round(streakScore + logsScore + moodScore + challengeScore);
}

function formatToday() {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export default function GlowReport() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const share = useShareSheet();
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState({ skin: null, hair: null, patterns: [] });
  const [stats, setStats] = useState({
    streak: 0,
    daysLogged: 0,
    routinesCompleted: 0,
    activeChallenges: 0,
    moods: [],
    challengeRows: [],
  });
  const [weeklySummary, setWeeklySummary] = useState(null);
  const isPremium = !!profile?.is_premium;

  // Hydrate stats + latest insights on mount
  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      const [logs, challenges, ins, weekly] = await Promise.all([
        supabase
          .from("progress_logs")
          .select("mood, type, log_date")
          .eq("user_id", profile.id),
        supabase
          .from("user_challenges")
          .select("completed, challenge_id")
          .eq("user_id", profile.id),
        supabase
          .from("ai_insights")
          .select("category, content, generated_at")
          .eq("user_id", profile.id)
          .order("generated_at", { ascending: false })
          .limit(20),
        supabase
          .from("ai_insights")
          .select("content, generated_at")
          .eq("user_id", profile.id)
          .eq("category", "weekly")
          .order("generated_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const logsArr = logs.data || [];
      const challengeArr = challenges.data || [];
      const uniqueDays = new Set(logsArr.map((l) => l.log_date)).size;

      setStats({
        streak: profile.streak || 0,
        daysLogged: uniqueDays,
        routinesCompleted: logsArr.filter((l) => l.type === "routine").length,
        activeChallenges: challengeArr.filter((c) => !c.completed).length,
        moods: logsArr.slice(-30).map((l) => l.mood).filter(Boolean),
        challengeRows: challengeArr,
      });

      // Pick latest insight per category
      const today = new Date().toISOString().slice(0, 10);
      const grouped = { skin: null, hair: null, patterns: [] };
      (ins.data || []).forEach((d) => {
        if (d.category === "skin" && !grouped.skin && d.generated_at.slice(0, 10) === today) grouped.skin = d;
        else if (d.category === "hair" && !grouped.hair && d.generated_at.slice(0, 10) === today) grouped.hair = d;
        else if (d.category === "patterns" && grouped.patterns.length < 3 && d.generated_at.slice(0, 10) === today)
          grouped.patterns.push(d);
      });
      setInsights(grouped);

      if (weekly.data) setWeeklySummary(weekly.data);
    })();
  }, [profile?.id, profile?.streak]);

  const glowScore = useMemo(() => calcGlowScore(stats), [stats]);
  // Weekly trend — fake-but-deterministic delta from streak strength
  const trend = useMemo(() => {
    const base = stats.streak >= 3 ? 5 : stats.streak > 0 ? 2 : 0;
    return base;
  }, [stats.streak]);

  const generate = async () => {
    if (!isPremium || !profile?.id) return;
    setBusy(true);
    const { data: logs } = await supabase
      .from("progress_logs")
      .select("note, type, mood, log_date")
      .eq("user_id", profile.id)
      .order("created_at", { ascending: false })
      .limit(14);

    const profileText = `Skin: ${profile.skin_type} / ${profile.skin_tone}. Hair: ${profile.hair_type}. Concerns: ${(profile.concerns || []).join(", ")}. Goals: ${(profile.goals || []).join(", ")}.`;
    const logsText = JSON.stringify(logs || []);

    const prompt = `You are a certified dermatologist and licensed trichologist for inclusive beauty. ${profileText}

Recent 14 logs: ${logsText}

Return ONLY JSON with this exact shape:
{
  "skin": { "title": "1-line catchy headline, max 50 chars", "body": "2-3 sentence skincare insight specific to this user's skin type and tone, with **bold** key ingredients and product mentions. Be specific not generic." },
  "hair": { "title": "1-line catchy headline, max 50 chars", "body": "2-3 sentence haircare insight specific to this user's hair type and concerns, with **bold** key ingredients/treatments." },
  "patterns": [
    "1-line behavioural pattern detected from the logs (e.g. 'Your skin struggles most on days you skip PM routine')",
    "1-line pattern about best days",
    "1-line pattern about timing/cadence"
  ]
}`;

    try {
      const json = await geminiJSON(prompt, { mode: "glow_report" });
      const today = new Date().toISOString();
      const rows = [
        { user_id: profile.id, category: "skin", content: JSON.stringify(json.skin || {}) },
        { user_id: profile.id, category: "hair", content: JSON.stringify(json.hair || {}) },
        ...(json.patterns || []).map((p) => ({
          user_id: profile.id,
          category: "patterns",
          content: p,
        })),
      ];
      const { data: inserted } = await supabase
        .from("ai_insights")
        .insert(rows)
        .select();
      const grouped = { skin: null, hair: null, patterns: [] };
      (inserted || []).forEach((d) => {
        if (d.category === "skin" && !grouped.skin) grouped.skin = d;
        else if (d.category === "hair" && !grouped.hair) grouped.hair = d;
        else if (d.category === "patterns") grouped.patterns.push(d);
      });
      setInsights(grouped);
    } catch (e) {
      console.error("[glow-report]", e);
    }
    setBusy(false);
  };

  // Parse skin/hair insight content (it's stored as JSON string in `content`)
  const parsedSkin = useMemo(() => parseInsight(insights.skin), [insights.skin]);
  const parsedHair = useMemo(() => parseInsight(insights.hair), [insights.hair]);

  return (
    <div className="-mt-px" data-testid="glow-report-page">
      {/* HEADER */}
      <div
        className="relative pt-6 px-5 pb-6 overflow-hidden"
        style={{ background: "linear-gradient(180deg, #C9748A 0%, #B8657A 100%)" }}
        data-testid="report-header"
      >
        {/* Decorative sparkle */}
        <Sparkles size={26} className="absolute top-5 right-5 text-white/40" />
        <h1 className="font-serif text-white text-3xl leading-tight">Your Glow Report</h1>
        <p className="text-white/85 text-sm font-light mt-0.5">
          Daily beauty intel — built from your skin + hair data
        </p>
        <div className="flex items-center gap-2 mt-3">
          <div
            className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full"
            data-testid="report-date-pill"
          >
            <Calendar size={11} /> {formatToday()}
          </div>
          <button
            onClick={() =>
              share.open({
                type: "glow_report",
                payload: { score: glowScore },
              })
            }
            data-testid="glow-report-share-btn"
            className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full"
          >
            <Share2 size={11} /> Share
          </button>
        </div>
      </div>

      <div className="px-5 pt-5 pb-24 space-y-5">
        {/* Glow Score */}
        <GlowScoreCard
          score={glowScore}
          trend={trend}
          isPremium={isPremium}
          onUnlock={() => navigate("/app/premium")}
        />

        {/* Generate button — premium */}
        {isPremium && (
          <button
            onClick={generate}
            disabled={busy}
            data-testid="generate-report-btn"
            className="w-full rounded-full py-3.5 text-white font-medium text-sm flex items-center justify-center gap-2 shadow-rose disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #C9748A 0%, #B8657A 100%)" }}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Sparkles size={14} /> Generate Today's Report
              </>
            )}
          </button>
        )}

        {/* Skincare Insight */}
        <InsightCard
          accent="#C9748A"
          label="Skincare insight"
          icon={<Sparkles size={12} />}
          title={parsedSkin?.title || "Tap Generate to receive today's skincare insight"}
          body={parsedSkin?.body}
          locked={!isPremium}
          onUnlock={() => navigate("/app/premium")}
          testId="skin-insight"
          chipMode="skin"
          skinType={profile?.skin_type}
        />

        {/* Haircare Insight */}
        <InsightCard
          accent="#C9975A"
          label="Haircare insight"
          icon={<Sparkles size={12} />}
          title={parsedHair?.title || "Tap Generate to receive today's haircare insight"}
          body={parsedHair?.body}
          locked={!isPremium}
          onUnlock={() => navigate("/app/premium")}
          testId="hair-insight"
          chipMode="hair"
          hairType={profile?.hair_type}
          concerns={profile?.concerns || []}
        />

        {/* Pattern Recognition */}
        <PatternRecognition
          patterns={insights.patterns}
          locked={!isPremium}
          onUnlock={() => navigate("/app/premium")}
        />

        {/* Glow Stats — FREE for all */}
        <div data-testid="glow-stats">
          <h2 className="font-serif text-2xl mb-3" style={{ color: "#2E1608" }}>
            Your Glow Stats
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <StatTile icon={Flame} value={stats.streak} label="Current streak days" color="rose" testId="stat-streak" />
            <StatTile value={stats.daysLogged} label="Total days logged" color="rose" testId="stat-logged" />
            <StatTile value={stats.routinesCompleted} label="Routines completed" color="rose" testId="stat-routines" />
            <StatTile
              icon={Trophy}
              value={stats.activeChallenges}
              label="Challenges active"
              color="gold"
              testId="stat-challenges"
            />
          </div>
        </div>

        {/* Weekly summary (premium only, shown if exists) */}
        {weeklySummary && (
          <div
            className="rounded-2xl p-5 text-white relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
            data-testid="weekly-summary"
          >
            <Crown size={16} className="mb-2" />
            <p className="text-[10px] uppercase tracking-[0.2em] opacity-85 mb-1">
              This week's glow summary
            </p>
            <p className="text-sm font-light leading-relaxed">{weeklySummary.content}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================ sub-components ============================ */

function parseInsight(row) {
  if (!row) return null;
  try {
    return JSON.parse(row.content);
  } catch {
    return { title: "", body: row.content };
  }
}

function GlowScoreCard({ score, trend, isPremium, onUnlock }) {
  const pct = isPremium ? score : 0;
  return (
    <div
      className="bg-white rounded-2xl p-5 border border-[#f0e8e4] relative overflow-hidden"
      data-testid="glow-score-card"
    >
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 shrink-0">
          <ConicScore percent={pct} />
          <div
            className="absolute inset-2 bg-white rounded-full flex flex-col items-center justify-center"
            style={{ boxShadow: "inset 0 0 0 1px #f0e8e4" }}
          >
            <span
              className="font-serif text-2xl leading-none"
              style={{ color: "#2E1608" }}
              data-testid="score-number"
            >
              {isPremium ? score : "—"}
            </span>
            <span className="text-[8px] uppercase tracking-wider text-cocoa/55 mt-0.5">
              Glow
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl leading-tight" style={{ color: "#2E1608" }}>
            Your Glow Score
          </h3>
          <p className="text-[11px] font-light text-cocoa/55 leading-snug mt-0.5">
            Based on your routine streak, logs and mood check-ins
          </p>
          {isPremium && trend > 0 && (
            <p
              className="text-[11px] font-medium mt-1.5 inline-flex items-center gap-1"
              style={{ color: "#1A5C35" }}
              data-testid="score-trend"
            >
              <TrendingUp size={11} /> +{trend} from last week
            </p>
          )}
        </div>
      </div>

      {!isPremium && (
        <div
          className="absolute inset-0 bg-white/70 backdrop-blur-md flex flex-col items-center justify-center text-center p-5"
          data-testid="score-locked"
        >
          <Crown size={20} style={{ color: "#C9975A" }} className="mb-1" />
          <p className="font-serif text-lg" style={{ color: "#2E1608" }}>
            Your Glow Score
          </p>
          <p className="text-[10px] text-cocoa/55 mb-2">Unlock with Premium</p>
          <button
            onClick={onUnlock}
            data-testid="score-unlock-btn"
            className="rounded-full text-white px-4 py-1.5 text-xs font-medium"
            style={{ background: "#C9748A" }}
          >
            Go Premium
          </button>
        </div>
      )}
    </div>
  );
}

function ConicScore({ percent }) {
  return (
    <div
      className="w-full h-full rounded-full"
      style={{
        background: `conic-gradient(#C9748A ${percent}%, #f0e8e4 ${percent}% 100%)`,
      }}
    />
  );
}

function InsightCard({ accent, label, icon, title, body, locked, onUnlock, testId, chipMode = "skin", skinType = null, hairType = null, concerns = [] }) {
  return (
    <div
      className="bg-white rounded-2xl p-5 border border-[#f0e8e4] relative"
      style={{ borderLeft: `4px solid ${accent}` }}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 mb-2">
        <span style={{ color: accent }}>{icon}</span>
        <p
          className="text-[10px] uppercase tracking-[0.22em] font-medium"
          style={{ color: accent }}
        >
          {label}
        </p>
      </div>
      <h3
        className="font-serif text-xl leading-tight mb-1"
        style={{ color: "#2E1608" }}
      >
        {title}
      </h3>
      {body && (
        <>
          <p
            className="text-sm font-light leading-relaxed text-cocoa"
            dangerouslySetInnerHTML={{
              __html: body.replace(
                /\*\*(.+?)\*\*/g,
                '<strong class="font-semibold text-cocoa">$1</strong>'
              ),
            }}
          />
          {!locked && (
            <ProductChips
              text={body}
              testIdPrefix={`${testId}-chip`}
              mode={chipMode}
              skinType={skinType}
              hairType={hairType}
              concerns={concerns}
            />
          )}
        </>
      )}
      {locked && (
        <div className="absolute inset-0 bg-white/65 backdrop-blur-md rounded-2xl flex flex-col items-center justify-center text-center p-4">
          <Crown size={18} style={{ color: "#C9975A" }} className="mb-1" />
          <p className="font-serif text-base mb-1" style={{ color: "#2E1608" }}>
            {label}
          </p>
          <p className="text-[10px] text-cocoa/55 mb-2">Unlock with Premium</p>
          <button
            onClick={onUnlock}
            data-testid={`${testId}-unlock`}
            className="rounded-full text-white px-4 py-1.5 text-xs font-medium"
            style={{ background: "#C9748A" }}
          >
            Go Premium
          </button>
        </div>
      )}
    </div>
  );
}

function PatternRecognition({ patterns, locked, onUnlock }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: "#FDE8F0" }}
      data-testid="pattern-recognition"
    >
      <div className="text-center mb-3">
        <Search size={18} className="mx-auto mb-1 text-rose" />
        <h3 className="font-serif text-lg" style={{ color: "#C9748A" }}>
          Pattern Recognition
        </h3>
      </div>
      {locked ? (
        <div className="text-center">
          <p className="text-xs font-light mb-3" style={{ color: "#C9748A" }}>
            Unlock with Premium
          </p>
          <button
            onClick={onUnlock}
            data-testid="patterns-unlock"
            className="rounded-full text-white px-5 py-2 text-xs font-medium shadow-rose"
            style={{ background: "#C9748A" }}
          >
            Go Premium
          </button>
        </div>
      ) : patterns.length === 0 ? (
        <p
          className="text-xs text-center font-light italic"
          style={{ color: "#C9748A" }}
        >
          Generate your first report to surface behavioural patterns.
        </p>
      ) : (
        <ul className="space-y-2">
          {patterns.map((p, i) => (
            <li
              key={i}
              data-testid={`pattern-${i}`}
              className="flex items-start gap-2 text-sm text-cocoa font-light"
            >
              <span className="w-1.5 h-1.5 mt-2 rounded-full bg-rose shrink-0" />
              <span>{p.content}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, value, label, color, testId }) {
  const valueColor = color === "gold" ? "#C9975A" : "#C9748A";
  return (
    <div
      className="bg-white rounded-2xl p-4 border border-[#f0e8e4]"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon size={18} style={{ color: valueColor }} />}
        <span
          className="font-serif text-3xl leading-none"
          style={{ color: valueColor }}
        >
          {value}
        </span>
      </div>
      <p className="text-[11px] text-cocoa/55 font-light mt-1.5">{label}</p>
    </div>
  );
}
